'use client';
import React from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

interface SubmitModalProps {
  unanswered: number;
  flagged: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function SubmitModal({ unanswered, flagged, onConfirm, onCancel }: SubmitModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm animate-fade-in">
      <div className="card-base p-6 max-w-md w-full mx-4 animate-scale-in shadow-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning-bg flex items-center justify-center">
              <AlertTriangle size={20} className="text-warning" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Submit Diagnostic Test?</h3>
              <p className="text-xs text-muted-foreground mt-0.5">This cannot be undone</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X size={16} className="text-muted-foreground" />
          </button>
        </div>

        <div className="space-y-2 mb-5">
          {unanswered > 0 ? (
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-danger-bg border border-danger/20">
              <AlertTriangle size={14} className="text-danger flex-shrink-0" />
              <p className="text-sm text-danger font-medium">
                {unanswered} question{unanswered !== 1 ? 's' : ''} unanswered — unanswered questions score zero
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-success-bg border border-accent/20">
              <CheckCircle2 size={14} className="text-accent flex-shrink-0" />
              <p className="text-sm text-accent font-medium">All 40 questions answered</p>
            </div>
          )}
          {flagged > 0 && (
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-warning-bg border border-warning/20">
              <AlertTriangle size={14} className="text-warning flex-shrink-0" />
              <p className="text-sm text-warning font-medium">
                {flagged} question{flagged !== 1 ? 's' : ''} still flagged for review
              </p>
            </div>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
          Once submitted, your diagnostic will be scored across all 9 question types and your personalised weak-area map will be generated immediately.
        </p>

        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-outline flex-1 justify-center">
            Return to Test
          </button>
          <button onClick={onConfirm} className="btn-primary flex-1 justify-center">
            Submit & View Results
          </button>
        </div>
      </div>
    </div>
  );
}